<template functional>
  <div
    v-tooltip="props.tooltip"
    class="relative flex flex-col justify-end feature-input"
    :class="$options.methods.mergeFunctionalRenderContextClasses(data, props.errors.length > 0 ? 'has-errors' : '')"
  >
    <!-- label -->
    <slot
      name="label"
      :input-id="`${props.label}_input`"
      :label="props.label"
    >
      <label
        v-if="props.label"
        :for="`${props.label}_input`"
        class="mb-1 mr-2 tracking-wider text-gray-800"
        :class="{
          hidden: props.hideLabel,
          [props.labelClass]: true
        }"
        v-text="props.label"
      />
    </slot>

    <!-- input -->
    <slot
      :id="`${props.label}_input`"
      :classes="$options.methods.getInputClasses(props, $options.methods.mergeFunctionalRenderContextClasses('', props.inputClass))"
      :has-errors="props.errors.length > 0"
    >
      <!-- default input if no addon is given -->
      <template v-if="!props.addon">
        <input
          :id="`${props.label}_input`"
          :ref="data.ref"
          v-bind="{
            type: 'text',
            ...data.attrs
          }"
          class="placeholder-gray-500 form-input"
          :class="$options.methods.getInputClasses(props, $options.methods.mergeFunctionalRenderContextClasses('', props.inputClass))"
          :type="props.type"
          v-on="{
            ...$options.methods.getFilteredListeners(listeners),
            input: (event) => {
              if (listeners.modelInput) {
                listeners.modelInput(event.target.value)
              }

              if (listeners.input) {
                listeners.input(event.target.value)
              }
            },
            focus: (event) => {
              if (listeners.focus) {
                listeners.focus(event)
              }

              if (props.selectOnFocus) {
                event.target.select()
              }
            }
          }"
        >
      </template>

      <template v-else>
        <component :is="injections.components.InputWithAddons">
          <template #default>
            <input
              :id="`${props.label}_input`"
              :ref="data.ref"
              v-bind="{
                type: 'text',
                ...data.attrs
              }"
              class="placeholder-gray-500 rounded-r-none form-input"
              :class="$options.methods.getInputClasses(props, $options.methods.mergeFunctionalRenderContextClasses('', props.inputClass))"
              :type="props.type"
              v-on="{
                ...$options.methods.getFilteredListeners(listeners),
                input: (event) => {
                  if (listeners.modelInput) {
                    listeners.modelInput(event.target.value)
                  }

                  if (listeners.input) {
                    listeners.input(event.target.value)
                  }
                }
              }"
            >
          </template>

          <template #right-addon>
            <div class="flex items-center px-3 text-gray-900 whitespace-no-wrap bg-gray-500 rounded-r">
              {{ props.addon }}
            </div>
          </template>
        </component>
      </template>
    </slot>

    <div
      v-if="props.type === 'search' && data.attrs.value"
      class="absolute inset-y-0 right-0 pt-6 pr-2 pointer-events-none"
    >
      <div class="h-full flex items-center">
        <button
          type="button"
          class="bg-gray-300 shadow-sm w-6 h-6 inline-flex items-center justify-center rounded-full pointer-events-auto text-gray-800 focus:outline-none hover:bg-gray-400"
          @click="$options.methods.clear(listeners)"
        >
          <mia-streamline-icon
            class="w-3 h-3 fill-current"
            src="close"
          />
        </button>
      </div>
    </div>

    <mia-fade-transition>
      <!-- eslint-disable vue/no-v-html -->
      <small
        v-if="props.errors.length > 0"
        class="absolute top-0 block leading-3 text-red-600"
        :style="{
          marginTop: `${props.label ? 67 : 45}px`
        }"
        v-html="props.errors[0]"
      />
      <!-- eslint-enable vue/no-v-html -->
    </mia-fade-transition>
  </div>
</template>

<script>
import { mergeFunctionalRenderContextClasses } from '@/helpers/utils'
import InputWithAddons from '@/components/ui/inputs/InputWithAddons.vue'

export default {
  // workaround for components in functional components
  inject: {
    components: {
      default: {
        InputWithAddons
      }
    }
  },
  model: {
    // Having a v-model binding, but also providing an @input event listener
    // to do additional things when the input changes results in the following binding for "input":
    // input: [null, null]. This seems to be a restriction when using functional components. Therefore
    // we have to account for that and use an alias instead.
    event: 'modelInput'
  },

  props: {
    label: {
      type: String,
      default: ''
    },

    hideLabel: {
      type: Boolean,
      default: false
    },

    labelClass: {
      type: [String, Object, Array],
      default: ''
    },

    errors: {
      type: Array,
      default() {
        return []
      }
    },

    inputClass: {
      type: [String, Object, Array],
      default: ''
    },

    // text right of input
    addon: {
      type: String,
      default: ''
    },

    tooltip: {
      type: String,
      default: ''
    },

    selectOnFocus: {
      type: Boolean,
      default: false
    },

    type: {
      type: String,
      default: 'text'
    }
  },
  methods: {
    mergeFunctionalRenderContextClasses,

    /**
     * Exclude the "modelInput" listener, if present, since that is a made up event listener name
     * which functions as alias for the "input" event, in order to workaround the issue described above.
     */
    getFilteredListeners(listeners) {
      const filteredListeners = {}

      Object.entries(listeners).forEach(([key, listener]) => {
        if (key !== 'modelInput') {
          filteredListeners[key] = listener
        }
      })

      return filteredListeners
    },

    getInputClasses(props, mergedClasses = {}) {
      return {
        'has-errors': props.errors.length > 0,
        ...mergedClasses
      }
    },

    clear(listeners) {
      if (!listeners) {
        return
      }

      if (listeners.modelInput) {
        listeners.modelInput('')
        return
      }

      if (listeners.input) {
        listeners.input('')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
input[type="file"].form-input {
  @apply text-base;

  height: 39px;

  &:focus {
    @apply outline-none;
    @apply shadow-outline;
  }

  &::-webkit-file-upload-button {
    @apply invisible;
  }

  &:before {
    @apply inline-flex;
    @apply items-center;
    @apply px-3;
    @apply py-1;
    @apply text-sm;
    @apply transition-colors;
    @apply duration-100;
    @apply ease-out;
    @apply bg-orange-100;
    @apply border;
    @apply border-orange-300;
    @apply rounded;
    @apply shadow;
    @apply text-orange-mia;
    @apply relative;

    content: 'Datei auswählen';
    top: -4px;
    height: 28px;
  }

  &:hover {
    @apply cursor-pointer;

    &:before {
      @apply bg-orange-mia;
      @apply text-white;
    }
  }
}

input[type="date"],
input[type="time"] {
  &.form-input {
    @apply text-base;

    line-height: normal;
    height: 39px;
  }
}

input[type="range"]{
  &.form-input {
    padding-left: 0;
    padding-right: 0;
  }
}

input.form-input:disabled {
  @apply opacity-50;
  @apply cursor-not-allowed;
}

.feature-input.has-errors .with-right-addon,
.feature-input.has-errors .with-left-addon {
  @apply rounded;
}

.feature-input.has-errors input {
  @apply shadow-outline-error;
}

.feature-input.has-errors .with-right-addon,
.feature-input.has-errors .with-left-addon {
  @apply shadow-outline-error;
}

.feature-input.has-errors .with-right-addon input,
.feature-input.has-errors .with-left-addon input {
  @apply shadow-none;
}
</style>
